import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { User } from '../types';
import { getCurrentUser } from '../api/api';
import { jwtDecode } from 'jwt-decode';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    isAuthenticated: boolean;
    login: (token: string, refreshToken?: string) => void;
    logout: () => void;
}

export const AuthContext = createContext<AuthContextType>({
    user: null,
    loading: true,
    isAuthenticated: false,
    login: () => { },
    logout: () => { },
});

export const useAuth = () => useContext(AuthContext);

// Auto-refresh utility
const refreshTokens = async (): Promise<{ token: string; refreshToken: string } | null> => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    try {
        const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/auth/refresh`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': import.meta.env.VITE_API_KEY || '',
            },
            body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) {
            throw new Error('Token refresh failed');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Token refresh failed:', error);
        return null;
    }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    // Check if token needs refresh (within 24 hours of expiration)
    const shouldRefreshToken = (token: string): boolean => {
        try {
            const decoded: any = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            const timeUntilExpiry = decoded.exp - currentTime;
            // Refresh if token expires within 24 hours (86400 seconds)
            return timeUntilExpiry < 86400;
        } catch {
            return true; // If we can't decode, assume we need to refresh
        }
    };

    useEffect(() => {
        const loadUser = async () => {
            const token = localStorage.getItem('token');
            const refreshToken = localStorage.getItem('refreshToken');

            if (!token) {
                setLoading(false);
                return;
            }

            try {
                // Check if token is expired or close to expiring
                const decoded: any = jwtDecode<User>(token);
                const currentTime = Date.now() / 1000;

                if (decoded.exp < currentTime) {
                    // Token is expired, try to refresh
                    if (refreshToken) {
                        console.log('Token expired, attempting refresh...');
                        const newTokens = await refreshTokens();
                        if (newTokens) {
                            localStorage.setItem('token', newTokens.token);
                            localStorage.setItem('refreshToken', newTokens.refreshToken);
                            const newDecoded = jwtDecode<User>(newTokens.token);
                            setUser({
                                id: newDecoded.id,
                                name: newDecoded.name,
                                email: newDecoded.email
                            });
                            console.log('Token refreshed successfully');
                        } else {
                            // Refresh failed, clear everything
                            localStorage.removeItem('token');
                            localStorage.removeItem('refreshToken');
                        }
                    } else {
                        localStorage.removeItem('token');
                    }
                    setLoading(false);
                    return;
                } else if (shouldRefreshToken(token) && refreshToken) {
                    // Token is still valid but should be refreshed proactively
                    console.log('Proactively refreshing token...');
                    const newTokens = await refreshTokens();
                    if (newTokens) {
                        localStorage.setItem('token', newTokens.token);
                        localStorage.setItem('refreshToken', newTokens.refreshToken);
                        console.log('Token proactively refreshed');
                    }
                }

                // Load user data
                const userData = await getCurrentUser();
                setUser(userData);
            } catch (error) {
                console.error('Error loading user:', error);
                // If getCurrentUser fails, try refreshing tokens first
                if (refreshToken) {
                    const newTokens = await refreshTokens();
                    if (newTokens) {
                        localStorage.setItem('token', newTokens.token);
                        localStorage.setItem('refreshToken', newTokens.refreshToken);
                        try {
                            const userData = await getCurrentUser();
                            setUser(userData);
                        } catch (retryError) {
                            console.error('Retry after refresh failed:', retryError);
                            localStorage.removeItem('token');
                            localStorage.removeItem('refreshToken');
                        }
                    } else {
                        localStorage.removeItem('token');
                        localStorage.removeItem('refreshToken');
                    }
                } else {
                    localStorage.removeItem('token');
                }
            } finally {
                setLoading(false);
            }
        };

        loadUser();
    }, []);

    // Set up automatic token refresh interval
    useEffect(() => {
        const interval = setInterval(async () => {
            const token = localStorage.getItem('token');
            const refreshToken = localStorage.getItem('refreshToken');

            if (token && refreshToken && shouldRefreshToken(token)) {
                console.log('Auto-refreshing token...');
                const newTokens = await refreshTokens();
                if (newTokens) {
                    localStorage.setItem('token', newTokens.token);
                    localStorage.setItem('refreshToken', newTokens.refreshToken);
                    console.log('Token auto-refreshed successfully');
                }
            }
        }, 60000); // Check every minute

        return () => clearInterval(interval);
    }, []);

    const login = useCallback(async (token: string, refreshToken?: string) => {
        localStorage.setItem('token', token);
        if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
        }

        if (token) {
            try {
                const decoded = jwtDecode<User>(token);
                // Set initial user data from token
                const initialUser = {
                    id: decoded.id,
                    name: decoded.name,
                    email: decoded.email
                };
                setUser(initialUser);

                // Immediately fetch complete user data including avatarUrl
                try {
                    const completeUserData = await getCurrentUser();
                    setUser(completeUserData);
                } catch (error) {
                    console.warn('Failed to fetch complete user data on login:', error);
                    // Keep the initial user data if complete fetch fails
                }
            } catch (e) {
                console.error('Invalid token', e);
            }
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                isAuthenticated: !!user,
                login,
                logout
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
